import { useTranslation } from 'react-i18next';
import Modal from './modal';
import Button from '../ui-kit/button';

export interface DraftConflictModalProps {
  isOpen: boolean;
  /** Epoch ms of the local draft's last save. */
  localSavedAt: number;
  /** Epoch ms of the server document's last_modified (0 if unknown). */
  serverSavedAt: number;
  /** Item count of the local draft (e.g. caption cues / segments). */
  localCount?: number;
  /** Item count of the server document. */
  serverCount?: number;
  /** Unit label for the counts, e.g. "cues" or "segments". */
  unit?: string;
  onKeepLocal: () => void;
  onLoadCloud: () => void;
  onViewCloud: () => void;
}

function formatWhen(ms: number): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return new Date(ms).toLocaleString();
  }
}

/**
 * Shown when a locally cached draft and the server document have BOTH advanced
 * independently (see resolveDraftRecovery → 'conflict'). The user must choose
 * which version to keep; neither can be picked automatically without risking
 * silent data loss. The modal is intentionally not dismissable — a choice is
 * required before the editor opens.
 */
export default function DraftConflictModal({
  isOpen,
  localSavedAt,
  serverSavedAt,
  localCount,
  serverCount,
  unit = 'items',
  onKeepLocal,
  onLoadCloud,
  onViewCloud
}: DraftConflictModalProps) {
  const { t } = useTranslation();

  const countLabel = (count?: number) =>
    count == null ? '' : ` · ${count} ${unit}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        /* blocking: a choice is required, outside-close is disabled */
      }}
      size="medium"
      closeOnOutsideClick={false}
      closeOnEsc={false}
      title={t('draft-conflict-title', 'This document changed elsewhere')}
      caption={t(
        'draft-conflict-caption',
        'You have unsaved local changes, but a different version was also saved to the cloud (another tab, device, or by support). Choose which one to keep.'
      )}
      footerActions={[
        <Button
          key="keep-local"
          variant="outline"
          size="small"
          label={t('draft-conflict-keep-local', 'Keep my local changes')}
          onClick={onKeepLocal}
        />,
        <Button
          key="load-cloud"
          variant="primary"
          size="small"
          label={t('draft-conflict-load-cloud', 'Load cloud version')}
          onClick={onLoadCloud}
        />
      ]}
    >
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              {t('draft-conflict-local', 'Your local changes')}
            </div>
            <div className="mt-1 text-sm">
              {formatWhen(localSavedAt)}
              {countLabel(localCount)}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              {t('draft-conflict-cloud', 'Cloud version')}
            </div>
            <div className="mt-1 text-sm">
              {formatWhen(serverSavedAt)}
              {countLabel(serverCount)}
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500 dark:text-zinc-400">
          {t(
            'draft-conflict-hint',
            'Keeping your local changes discards the cloud version. Loading the cloud version discards your unsaved local changes. Not sure?'
          )}{' '}
          <button
            type="button"
            onClick={onViewCloud}
            className="font-medium text-blue-600 underline hover:text-blue-700"
          >
            {t(
              'draft-conflict-view-cloud',
              'View the cloud version in a new tab'
            )}
          </button>
          .
        </p>
      </div>
    </Modal>
  );
}
